import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';
import { permissionService } from '../../services/permissionService';
import { Permission, PermissionType } from '../../types/permissions';
import { PermissionFormModal, PermissionFormValues } from './components/PermissionFormModal';

const moduleOptions = [
  'Todos',
  'Usuários',
  'Equipes',
  'Perfis',
  'Permissões',
  'Relatórios',
  'Documentos',
  'Categorias',
  'Configurações',
  'Logs',
  'API'
];

const typeOptions = ['Todos', 'Leitura', 'Escrita', 'Execução', 'Administração'];
const statusOptions = ['Todos', 'Ativo', 'Inativo'];

export const PermissionsPage: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [moduleFilter, setModuleFilter] = useState('Todos');
  const [typeFilter, setTypeFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [showSystem, setShowSystem] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const data = await permissionService.getAdminPermissions();
      setPermissions(data);
    } catch (error: any) {
      let msg = 'Erro ao carregar permissões.';
      if (error.response && error.response.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      addToast(msg, 'error');
    }
  };

  const filteredPermissions = permissions.filter(permission =>
    (!permission.isSystem || showSystem) &&
    (moduleFilter === 'Todos' || permission.module === moduleFilter) &&
    (typeFilter === 'Todos' || permission.type === typeFilter) &&
    (statusFilter === 'Todos' || (statusFilter === 'Ativo' ? permission.isActive : !permission.isActive))
  );

  const handleOpenCreate = () => {
    setModalError(null);
    setModalMode('create');
    setEditingPermission(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (permission: Permission) => {
    if (permission.isSystem) return;
    setModalError(null);
    setModalMode('edit');
    setEditingPermission(permission);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalError(null);
    setEditingPermission(null);
  };

  const initialValues: PermissionFormValues =
    modalMode === 'edit' && editingPermission
      ? {
          name: editingPermission.name ?? '',
          description: editingPermission.description ?? '',
          module: editingPermission.module ?? '',
          type: (editingPermission.type as PermissionType) ?? '',
          isActive: editingPermission.isActive ?? true
        }
      : {
          name: '',
          description: '',
          module: '',
          type: '',
          isActive: true
        };

  const handleSubmit = async (values: PermissionFormValues) => {
    try {
      setIsSubmitting(true);
      setModalError(null);
      if (modalMode === 'edit') {
        if (!editingPermission) return;
        await permissionService.updatePermission(editingPermission.id, {
          name: values.name,
          description: values.description,
          module: values.module,
          type: values.type as PermissionType,
          isActive: values.isActive
        });
        addToast('Permissão atualizada com sucesso!', 'success');
      } else {
        await permissionService.createPermission({
          name: values.name,
          description: values.description,
          module: values.module,
          type: values.type as PermissionType,
          isActive: values.isActive,
          isSystem: false
        });
        addToast('Permissão criada com sucesso!', 'success');
      }
      await fetchPermissions();
      handleCloseModal();
    } catch (error: any) {
      let msg = modalMode === 'edit' ? 'Erro ao atualizar permissão.' : 'Erro ao criar permissão.';
      if (error.response?.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      setModalError(msg);
      addToast(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (permission: Permission) => {
    if (permission.isSystem) {
      addToast('Não é permitido remover permissões de sistema.', 'error');
      return;
    }
    if (!window.confirm('Tem certeza que deseja excluir esta permissão?')) return;
    try {
      await permissionService.deletePermission(permission.id);
      addToast('Permissão removida com sucesso!', 'success');
      await fetchPermissions();
    } catch (error: any) {
      let msg = 'Erro ao remover permissão.';
      if (error.response?.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      addToast(msg, 'error');
    }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Permissões</h1>
        <Button onClick={handleOpenCreate}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nova Permissão
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="flex flex-wrap gap-2 items-center">
            <select
              className="w-40 border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={moduleFilter}
              onChange={e => setModuleFilter(e.target.value)}
            >
              {moduleOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <select
              className="w-40 border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              {typeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <select
              className="w-40 border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              {statusOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <label className="inline-flex items-center ml-4 shrink-0">
              <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-blue-600"
                checked={showSystem}
                onChange={e => setShowSystem(e.target.checked)}
              />
              <span className="ml-2 text-gray-700">Exibir permissões do sistema</span>
            </label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Nome</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Descrição</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Módulo</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Tipo</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Status</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    Nenhuma permissão encontrada.
                  </td>
                </tr>
              ) : (
                filteredPermissions.map(permission => (
                  <tr key={permission.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">
                      <div className="flex items-center space-x-2">
                        <span>{permission.name}</span>
                        {permission.isSystem && (
                          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded">
                            Sistema
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{permission.description}</td>
                    <td className="py-3 px-4">{permission.module}</td>
                    <td className="py-3 px-4">
                      <span className={`${
                        permission.type === 'Leitura' ? 'bg-blue-100 text-blue-800' :
                        permission.type === 'Escrita' ? 'bg-green-100 text-green-800' :
                        permission.type === 'Execução' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-purple-100 text-purple-800'
                      } text-xs font-medium px-2.5 py-0.5 rounded`}>
                        {permission.type}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`${
                        permission.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-700'
                      } text-xs font-medium px-2.5 py-0.5 rounded`}>
                        {permission.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/admin/permissions/${permission.id}/roles`)}
                          title="Gerenciar Perfis"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </Button>
                        {!permission.isSystem ? (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleOpenEdit(permission)}
                              title="Editar"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDelete(permission)}
                              title="Excluir"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PermissionFormModal
        isOpen={isModalOpen}
        title={modalMode === 'edit' ? 'Editar Permissão' : 'Nova Permissão'}
        moduleOptions={moduleOptions}
        typeOptions={typeOptions}
        initialValues={initialValues}
        isSubmitting={isSubmitting}
        errorMessage={modalError}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
}; 
